import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, AlertTriangle, EyeOff } from "lucide-react";

import { fetchDashboard, fetchPublishers, type PublisherSummary } from "../api/issues";
import { fetchCoverage, totalsOf } from "../api/coverage";
import { useAuth } from "../auth/AuthProvider";
import { Spinner } from "../components/Spinner";
import { fmtIST, shortDate } from "../lib/format";
import { categoryKey, CATEGORY_LABEL, CATEGORY_ORDER, type Category } from "../lib/publishers";
import { useBusinessDate } from "../lib/businessDate";

/* ──────────────────────────────────────────────────────────────────────
   Today's Desk — dense, no decoration. One short header line + a tab
   strip + a single table. Each row = one publisher.
   ────────────────────────────────────────────────────────────────────── */

interface Row {
  pub: PublisherSummary;
  cat: Exclude<Category, "all"> | null;   // null = un-categorised in DB (defensive)
  todayIssues: number;
  todayPages:  number;
  todayBytes:  number;
  /** Earliest crawl-fetch UTC timestamp for any issue on the display date. */
  firstFetchedAt: string | null;
}

function fmtBytes(b: number): string {
  if (b > 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`;
  if (b > 1024 ** 2) return `${(b / 1024 ** 2).toFixed(0)} MB`;
  if (b > 1024)      return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

export function Home() {
  const { me } = useAuth();
  const { date } = useBusinessDate();

  // Analytics columns (Ed / Iss / Pages / Avg) are for operators only — admins
  // and super admins. The "User" role sees just identity + Size / Last / Fetched.
  // Both grid templates are written as full literals so Tailwind JIT emits them.
  const showAnalytics = me?.group_name === "SuperAdmin" || me?.group_name === "Admin";
  const gridCls = showAnalytics
    ? "grid-cols-[26px,1fr,82px,68px,60px,72px,52px,64px,64px,68px,18px]"
    : "grid-cols-[26px,1fr,82px,64px,64px,68px,18px]";

  const dash = useQuery({
    queryKey: ["dashboard", date ?? "auto"],
    queryFn:  () => fetchDashboard(date ?? undefined),
  });

  const pubs = useQuery({
    queryKey: ["publishers", date ?? "auto"],
    queryFn:  () => fetchPublishers({
      includeUncrawled: false,
      businessDate: date ?? undefined,
    }),
  });

  const [tab, setTab] = useState<Category>("all");

  // Coverage — what did NOT arrive. Operators only; the table below hides
  // publishers with zero issues, so this strip is the only place a dead
  // publisher shows up on Today.
  const cov = useQuery({
    queryKey: ["coverage", date ?? "today"],
    queryFn:  () => fetchCoverage(date ?? undefined),
    enabled:  showAnalytics,
    staleTime: 120_000,
  });

  /* Merge publisher list with today-by-publisher dashboard rows.
     Only keep publishers that actually had issues on the displayed date —
     a paper that didn't run / couldn't be crawled shouldn't show up at all. */
  const rows: Row[] = useMemo(() => {
    if (!pubs.data) return [];
    const dashByName = new Map(
      (dash.data?.by_publisher ?? []).map(d => [d.channel_name, d])
    );
    const out: Row[] = [];
    for (const p of pubs.data) {
      const d = dashByName.get(p.channel_name);
      if (!d || d.issue_count <= 0) continue;
      out.push({
        pub: p,
        cat: categoryKey(p.category),
        todayIssues: d.issue_count,
        todayPages:  d.page_count,
        todayBytes:  d.bytes,
        firstFetchedAt: d.first_fetched_at ?? null,
      });
    }
    return out;
  }, [pubs.data, dash.data]);

  /* Tab counts shown in the chip labels */
  const tabCounts = useMemo(() => {
    const c: Record<Category, number> = {
      all: rows.length, regional: 0, mainline: 0, financial: 0, periodical: 0,
    };
    for (const r of rows) {
      if (r.cat) c[r.cat]++;
    }
    return c;
  }, [rows]);

  /* Filter by current tab */
  const filtered = useMemo(() => {
    return tab === "all" ? rows : rows.filter(r => r.cat === tab);
  }, [rows, tab]);

  if (!me) return null;

  return (
    <div className="flex flex-col gap-3 animate-fade-up">
      {/* ── Single-line masthead — date + totals ───────────────────── */}
      <header className="flex items-baseline justify-between flex-wrap gap-2 pb-1 border-b"
              style={{ borderColor: "var(--rule)" }}>
        <div className="flex items-baseline gap-2 flex-wrap">
          <h1 className="font-display text-[20px] font-semibold tracking-tight leading-none">
            Today
          </h1>
          {dash.data && (
            <>
              <span className="font-mono text-[11px] text-[var(--fg-faint)]">·</span>
              <span className="font-mono text-[11px] uppercase tracking-folio text-[var(--fg-muted)]">
                {shortDate(dash.data.display_date)}
              </span>
              {!dash.data.is_today && dash.data.total_issues > 0 && !date && (
                <span className="np-chip np-chip--admin"
                      title="No crawl yet for today — showing the most recent business date">
                  late edition
                </span>
              )}
              {dash.data.total_issues > 0 && (
                <>
                  <span className="font-mono text-[11px] text-[var(--fg-faint)]">·</span>
                  <span className="font-mono text-[12px] text-[var(--fg-muted)] tabular-nums">
                    {showAnalytics && <>
                      <strong className="text-[var(--fg)]">{dash.data.total_issues}</strong> iss
                      <span className="text-[var(--fg-faint)] mx-1">/</span>
                      <strong className="text-[var(--fg)]">{dash.data.total_pages.toLocaleString()}</strong> p
                      <span className="text-[var(--fg-faint)] mx-1">/</span>
                    </>}
                    <strong className="text-[var(--fg)]">{fmtBytes(dash.data.total_bytes)}</strong>
                    <span className="text-[var(--fg-faint)] mx-1">/</span>
                    <strong className="text-[var(--fg)]">{dash.data.publishers_today}</strong> pubs
                  </span>
                </>
              )}
            </>
          )}
        </div>
      </header>

      {/* ── Tabs + crawler toggle ──────────────────────────────────── */}
      <nav
        role="tablist"
        aria-label="Publisher category"
        className="flex items-center gap-px border-b mb-2"
        style={{ borderColor: "var(--rule)" }}
      >
        {CATEGORY_ORDER.map((c) => {
          const active = tab === c;
          const n = tabCounts[c];
          return (
            <button
              key={c}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(c)}
              className="px-3 py-1.5 font-mono text-[11px] uppercase tracking-folio relative transition-colors"
              style={{
                color: active ? "var(--fg)" : "var(--fg-muted)",
                background: active ? "var(--bg-card)" : "transparent",
              }}
            >
              {CATEGORY_LABEL[c]}
              <span className="text-[var(--fg-faint)] ml-1.5 tabular-nums">{n}</span>
              {active && (
                <span
                  aria-hidden
                  className="absolute left-0 right-0 -bottom-[1px] h-[2px]"
                  style={{ background: "var(--accent)" }}
                />
              )}
            </button>
          );
        })}
        <div className="flex-1" />

        {pubs.isFetching && (
          <span className="font-mono text-[10px] text-[var(--fg-faint)] mr-3">refreshing…</span>
        )}
      </nav>

      {/* Coverage strip — missing editions/publishers for the display date */}
      {showAnalytics && cov.data && (() => {
        const t = totalsOf(cov.data);
        const gaps = cov.data.publishers.filter(p => p.status !== "ok" && p.status !== "scheduled_off");
        const hidden = cov.data.publishers.filter(p => p.produced === 0 && p.status !== "scheduled_off");
        if (t.missing === 0) return (
          <div className="font-mono text-[11px] text-[var(--fg-muted)] flex items-center gap-2">
            <span className="text-[var(--fg)]">{t.produced}/{t.expected}</span> expected editions produced — full coverage.
          </div>
        );
        return (
          <div className="np-banner flex flex-wrap items-center gap-x-3 gap-y-1">
            <EyeOff size={14} className="text-[var(--accent)] shrink-0" />
            <span className="font-mono text-[12px]">
              <strong className="text-[var(--accent)]">{t.missing}</strong> of {t.expected} expected editions missing
              {hidden.length > 0 && <> · <strong>{hidden.length}</strong> publisher{hidden.length === 1 ? "" : "s"} absent from the table below</>}
              {t.publishers_dead > 0 && <> · <strong className="text-[var(--accent)]">{t.publishers_dead}</strong> dead ≥7d</>}
              {t.publishers_auth_blocked > 0 && <> · <strong className="text-[var(--accent)]">{t.publishers_auth_blocked}</strong> auth-blocked</>}
              {cov.data.crawl_window_open && <span className="text-[var(--fg-faint)]"> · crawl window open</span>}
            </span>
            <span className="font-mono text-[11px] text-[var(--fg-muted)] truncate">
              {gaps.slice(0, 8).map(p => `${p.channel_name} ${p.produced}/${p.expected}`).join(" · ")}{gaps.length > 8 ? " · …" : ""}
            </span>
            <Link to={`/crawler-ops/coverage${date ? `?date=${date}` : ""}`} className="np-link font-mono text-[11px] ml-auto whitespace-nowrap">
              see what's missing →
            </Link>
          </div>
        );
      })()}

      {/* Empty-day banner — only when a date was explicitly picked */}
      {!dash.isLoading && dash.data && dash.data.total_issues === 0 && date && (
        <div className="np-banner flex items-center gap-2">
          <AlertTriangle size={14} className="text-[var(--accent)] shrink-0" />
          <span>
            <strong>No issues for {shortDate(date)}.</strong>{" "}
            The crawler may not have run that day, or the date is in the future.
          </span>
        </div>
      )}

      {/* ── Dense table ─────────────────────────────────────────────── */}
      {(dash.isLoading || pubs.isLoading) && (
        <div className="py-8 flex justify-center"><Spinner label="Loading" /></div>
      )}

      {filtered.length > 0 && (
        <section className="np-card overflow-hidden">
          {/* header row */}
          <div
            className={`grid ${gridCls} gap-2 px-3 py-2 border-b
                       font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]`}
            style={{ borderColor: "var(--rule)" }}
          >
            <div className="text-right">#</div>
            <div>Publisher</div>
            <div>Category</div>
            {showAnalytics && <>
              <div className="text-right" title="Crawlable / Total editions">Ed</div>
              <div className="text-right" title="Issues captured for the display date">Iss</div>
              <div className="text-right" title="Total page artifacts captured">Pages</div>
              <div className="text-right" title="Avg pages per issue">Avg</div>
            </>}
            <div className="text-right">Size</div>
            <div className="text-right" title="Most recent business date for any edition">Last</div>
            <div className="text-right" title="When the earliest issue for this date was fetched (IST)">Fetched</div>
            <div />
          </div>

          <ul>
            {filtered.map((r, i) => {
              const avg = r.todayIssues > 0
                ? Math.round(r.todayPages / r.todayIssues)
                : 0;
              return (
                <li
                  key={r.pub.channel_id}
                  className="border-b transition-colors hover:bg-[var(--bg-inset)] last:border-b-0"
                  style={{ borderColor: "var(--rule)" }}
                >
                  <Link
                    to={`/browse/${r.pub.channel_id}`}
                    className={`grid ${gridCls} gap-2 items-baseline px-3 py-2 group`}
                  >
                    <div className="font-mono text-[10px] tabular-nums text-[var(--fg-faint)] text-right">
                      {String(i + 1).padStart(2, "0")}
                    </div>

                    <div className="min-w-0 flex items-baseline gap-2">
                      <span className="font-display text-[15px] leading-none truncate">
                        {r.pub.channel_name}
                      </span>
                      <span className="font-mono text-[9px] text-[var(--fg-faint)] tabular-nums">
                        ch{r.pub.channel_id}
                      </span>
                    </div>

                    <div>
                      <span className="np-chip"
                            style={{
                              background: "transparent",
                              color: "var(--fg-muted)",
                              borderColor: "var(--rule)",
                            }}>
                        {r.cat ? CATEGORY_LABEL[r.cat] : "—"}
                      </span>
                    </div>

                    {showAnalytics && <>
                    <div className="text-right font-mono text-[12px] tabular-nums"
                         title={r.pub.crawlable_count === 0
                                ? "No crawler-enabled editions yet"
                                : `${r.pub.crawlable_count} crawlable of ${r.pub.edition_count} total`}>
                      {r.pub.crawlable_count === 0 ? (
                        <>
                          <span className="text-[var(--accent)]">0</span>
                          <span className="text-[var(--fg-faint)]">/{r.pub.edition_count}</span>
                          <span className="ml-1.5 inline-block px-1 font-mono text-[8px] uppercase tracking-folio"
                                style={{ background: "var(--accent-soft)", color: "var(--accent)" }}>
                            pending
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-[var(--fg)]">{r.pub.crawlable_count}</span>
                          <span className="text-[var(--fg-faint)]">/{r.pub.edition_count}</span>
                        </>
                      )}
                    </div>

                    <Cell value={r.todayIssues > 0 ? r.todayIssues : null} />
                    <Cell value={r.todayPages > 0 ? r.todayPages : null} fmt="num" />
                    <Cell value={avg > 0 ? avg : null} />
                    </>}
                    <Cell value={r.todayBytes > 0 ? fmtBytes(r.todayBytes) : null} small />
                    <Cell value={r.pub.latest_business_date ? shortDate(r.pub.latest_business_date) : null} small />
                    <Cell value={fmtIST(r.firstFetchedAt)} small />

                    <div className="text-right">
                      <ArrowUpRight size={12} className="text-[var(--fg-faint)] group-hover:text-[var(--accent)] transition-colors" />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* totals footer — re-totals the visible tab so users always know what they're looking at */}
          {(() => {
            const sum = filtered.reduce((a, r) => ({
              issues: a.issues + r.todayIssues,
              pages:  a.pages  + r.todayPages,
              bytes:  a.bytes  + r.todayBytes,
              ed:     a.ed     + r.pub.edition_count,
              crawl:  a.crawl  + r.pub.crawlable_count,
            }), { issues: 0, pages: 0, bytes: 0, ed: 0, crawl: 0 });
            const avg = sum.issues > 0 ? Math.round(sum.pages / sum.issues) : 0;
            return (
              <div
                className={`grid ${gridCls} gap-2 px-3 py-2 border-t
                           font-mono text-[11px] tabular-nums text-[var(--fg)]`}
                style={{ borderColor: "var(--rule-strong)", background: "var(--bg-inset)" }}
              >
                <div className="text-right text-[var(--fg-faint)]" aria-hidden>Σ</div>
                <div className="text-[var(--fg-muted)] uppercase tracking-folio text-[10px]">
                  {tab === "all" ? "Total" : `${CATEGORY_LABEL[tab]} total`}
                  <span className="text-[var(--fg-faint)] ml-2">{filtered.length} pubs</span>
                </div>
                <div />
                {showAnalytics && <>
                  <div className="text-right">
                    <span>{sum.crawl}</span>
                    <span className="text-[var(--fg-faint)]">/{sum.ed}</span>
                  </div>
                  <div className="text-right">{sum.issues || "—"}</div>
                  <div className="text-right">{sum.pages > 0 ? sum.pages.toLocaleString() : "—"}</div>
                  <div className="text-right">{avg > 0 ? avg : "—"}</div>
                </>}
                <div className="text-right text-[var(--fg-muted)] text-[11px]">{sum.bytes > 0 ? fmtBytes(sum.bytes) : "—"}</div>
                <div />
                <div />
                <div />
              </div>
            );
          })()}
        </section>
      )}

      {filtered.length === 0 && !pubs.isLoading && !dash.isLoading && (
        <div className="np-card-inset px-5 py-8 text-center">
          <div className="font-display text-[18px] mb-1">
            {dash.data && dash.data.total_issues === 0
              ? "Nothing crawled for this date yet"
              : "No publishers with issues in this category"}
          </div>
          <div className="font-sans text-[12px] text-[var(--fg-muted)]">
            {dash.data && dash.data.total_issues === 0
              ? "Pick a different date, or wait for the crawler to run."
              : "Switch tabs to see other publisher groups."}
          </div>
        </div>
      )}
    </div>
  );
}

/** A tight numeric/text cell that renders `—` when value is null/0. */
function Cell({
  value, fmt, small,
}: {
  value: number | string | null;
  fmt?: "num";
  small?: boolean;
}) {
  const cls = small
    ? "text-right font-mono text-[11px] tabular-nums text-[var(--fg-muted)]"
    : "text-right font-mono text-[13px] tabular-nums text-[var(--fg)]";
  if (value === null || value === 0) {
    return <div className={cls}><span className="text-[var(--fg-faint)]">—</span></div>;
  }
  if (typeof value === "number" && fmt === "num") {
    return <div className={cls}>{value.toLocaleString()}</div>;
  }
  return <div className={cls}>{value}</div>;
}
